import Header from '../../components/public/Header';
import { currentPublicBrand } from '../../lib/publicBrand';
import CareDockLegalPending from './CareDockLegalPending';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail } from 'lucide-react';

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-heading text-xl font-semibold text-text-primary mb-3">{title}</h2>
    <div className="text-text-muted leading-relaxed space-y-3">{children}</div>
    <hr className="border-[#E4E8EB] mt-6" />
  </div>
);

export default function PrivacyPage() {
  if (currentPublicBrand().isCareDock) return <CareDockLegalPending type="privacy" />;

  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      {/* Hero */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-4 leading-tight">
            Privacy Policy
          </h1>
          <p className="text-lg text-text-muted leading-relaxed">
            We are committed to protecting your privacy and handling your personal information transparently and responsibly.
          </p>
          <p className="text-sm text-text-muted mt-4">Last updated: June 2026</p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-0">
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 space-y-6">

            <Section title="1. Who we are">
              <p>
                <strong>Data Controller:</strong> Osabea Healthcare Solutions Limited<br />
                <strong>Registered Office:</strong> Suite FA4D, 1 St Faith's Street, Maidstone, Kent, ME14 1LH<br />
                <strong>Company Registration:</strong> England and Wales<br />
                <strong>CQC Registration:</strong> Provider ID 1-18664797672<br />
                <strong>ICO Registration:</strong> Registered with the Information Commissioner's Office (ICO) in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
              <p>
                Our current service is supplying healthcare and support staff to other care providers. Osabea is separately registered with CQC for the regulated activity of Personal care; that registration does not mean direct care services are currently being delivered. We will update this notice when our services change.
              </p>
              <p>
                For privacy enquiries or to exercise your rights, contact us at: <a href="mailto:info@osabeacares.co.uk" className="text-primary underline">info@osabeacares.co.uk</a>
              </p>
            </Section>

            <Section title="2. Personal data we collect">
              <p>We collect and process different categories of personal data depending on your relationship with us:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>Website visitors:</strong> IP address, pages visited, browser type, and enquiry form data (name, phone, email, message).</li>
                <li><strong>People receiving directly managed care and their families (if that service begins):</strong> Relevant contact, health, care needs, and next-of-kin information where necessary for that care.</li>
                <li><strong>Job applicants and workers:</strong> Full name, address, date of birth, National Insurance number, employment history, references, right to work documentation, DBS certificate information, training records, emergency contact details, and bank details for payroll.</li>
                <li><strong>Healthcare organisations contacting us for staffing:</strong> Contact person name, job title, organisation name, address, phone, and email.</li>
              </ul>
            </Section>

            <Section title="3. Special category and criminal conviction data">
              <p>
                In recruiting and supplying healthcare workers, we may process <strong>special category personal data</strong> relating to staff health and <strong>criminal conviction data</strong> relating to appropriate DBS checks. We may also process health information about people receiving care if and when Osabea directly delivers that service.
              </p>
              <p>
                We process health data under Article 9(2)(h) UK GDPR (health or social care purposes) and Schedule 1, Part 1 of the Data Protection Act 2018. DBS check information is processed under Schedule 1, Part 2, paragraph 6 of the Data Protection Act 2018 (employment purposes — safeguarding).
              </p>
              <p>
                We only process special category and criminal conviction data where strictly necessary and apply appropriate safeguards including access controls, staff training, and confidentiality obligations.
              </p>
            </Section>

            <Section title="4. Legal bases for processing">
              <p>We rely on the following lawful bases under UK GDPR Article 6:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>Contract performance (Article 6(1)(b)):</strong> To arrange agreed staffing services and fulfil employment or engagement contracts, and any directly contracted care service when applicable.</li>
                <li><strong>Legal obligation (Article 6(1)(c)):</strong> To comply with employment law, CQC regulatory requirements, safeguarding obligations and HMRC obligations.</li>
                <li><strong>Legitimate interests (Article 6(1)(f)):</strong> For business administration, service improvement, fraud prevention, and managing our website. We have carried out Legitimate Interests Assessments where required.</li>
                <li><strong>Vital interests (Article 6(1)(d)):</strong> In emergency situations involving a care client or worker.</li>
                <li><strong>Consent (Article 6(1)(a)):</strong> Where you have given us specific, freely given, informed consent — for example, for marketing communications. You may withdraw consent at any time.</li>
              </ul>
            </Section>

            <Section title="5. How we use your personal data">
              <ul className="list-disc list-inside space-y-1">
                <li>Responding to staffing enquiries and, when applicable, care enquiries or assessments</li>
                <li>Arranging healthcare staffing assignments and, if separately commenced, directly managed care services</li>
                <li>Recruiting, employing, and managing our workforce</li>
                <li>Conducting DBS checks and verifying right to work</li>
                <li>Meeting our CQC regulatory obligations</li>
                <li>Maintaining care records and supporting continuity of care where Osabea directly provides those services</li>
                <li>Processing payroll and managing worker payments</li>
                <li>Handling complaints and safeguarding concerns</li>
                <li>Operating and improving our website and digital services</li>
                <li>Communicating with you about our services (where you have consented or have a legitimate interest)</li>
              </ul>
              <p>We do not sell your personal data to any third party. We do not use your data for automated decision-making that produces significant legal effects without human review.</p>
            </Section>

            <Section title="6. Who we share your data with">
              <p>We share personal data only where necessary, with:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>NHS and GP surgeries:</strong> Where required to deliver or coordinate care.</li>
                <li><strong>Local authorities:</strong> For safeguarding referrals, care funding, or compliance with statutory duties.</li>
                <li><strong>The Disclosure and Barring Service (DBS):</strong> For criminal record checks.</li>
                <li><strong>HMRC:</strong> For payroll and tax obligations.</li>
                <li><strong>CQC:</strong> As our regulatory body, as required by law.</li>
                <li><strong>IT and software providers:</strong> Cloud hosting and care management software providers acting as data processors under written Data Processing Agreements.</li>
                <li><strong>Insurance providers:</strong> Where required to process claims.</li>
                <li><strong>Our legal and financial advisers:</strong> Where required to obtain professional advice.</li>
              </ul>
              <p>All third parties are required to handle your data lawfully and in accordance with our instructions.</p>
            </Section>

            <Section title="7. International transfers">
              <p>
                We aim to keep your personal data within the UK and European Economic Area (EEA). Where any transfer outside the UK is necessary, we ensure appropriate safeguards are in place — such as the UK's International Data Transfer Agreement (IDTA) or adequacy decisions — in accordance with UK GDPR Chapter V.
              </p>
            </Section>

            <Section title="8. Data retention">
              <p>We retain personal data for as long as necessary for the purpose for which it was collected and in line with legal and regulatory requirements:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>Care client records:</strong> A minimum of 8 years after care ends (or until the person's 25th birthday if under 18 when care began), in line with NHS and CQC guidance.</li>
                <li><strong>Employee and worker records:</strong> 6 years after employment ends, in line with HMRC and employment law requirements.</li>
                <li><strong>DBS check information:</strong> Maximum 6 months after the recruitment decision, unless a longer period is justified in specific circumstances.</li>
                <li><strong>Job applicant data (unsuccessful):</strong> Up to 6 months after the recruitment process ends, then securely deleted.</li>
                <li><strong>Website enquiry data:</strong> Up to 2 years, or longer if we enter a service relationship.</li>
                <li><strong>Financial records:</strong> 6 years from the end of the relevant tax year, as required by HMRC.</li>
              </ul>
              <p>After retention periods expire, data is securely and permanently deleted or anonymised.</p>
            </Section>

            <Section title="9. Your data protection rights">
              <p>Under UK GDPR, you have the following rights:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                <li><strong>Right of access (Subject Access Request):</strong> To receive a copy of the personal data we hold about you.</li>
                <li><strong>Right to rectification:</strong> To have inaccurate data corrected or incomplete data completed.</li>
                <li><strong>Right to erasure ("right to be forgotten"):</strong> To request deletion of your data in certain circumstances.</li>
                <li><strong>Right to restriction:</strong> To request that we restrict processing while a query is resolved.</li>
                <li><strong>Right to data portability:</strong> To receive your data in a structured, machine-readable format.</li>
                <li><strong>Right to object:</strong> To object to processing based on legitimate interests or for direct marketing (absolute right).</li>
                <li><strong>Rights related to automated decision-making and profiling:</strong> Not to be subject to solely automated decisions with significant effects.</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at <a href="mailto:info@osabeacares.co.uk" className="text-primary underline">info@osabeacares.co.uk</a>. We will respond within <strong>one calendar month</strong>. We do not charge a fee for standard requests.
              </p>
              <p>
                You have the right to lodge a complaint with the Information Commissioner's Office (ICO) at any time: <a href="https://ico.org.uk/make-a-complaint" target="_blank" rel="noopener noreferrer" className="text-primary underline">ico.org.uk/make-a-complaint</a> or by calling the ICO helpline on <strong>0303 123 1113</strong>.
              </p>
            </Section>

            <Section title="10. Cookies and website tracking">
              <p>
                Our website uses <strong>essential cookies</strong> to keep you signed in and make core features work. These cookies are strictly necessary and do not require your consent under PECR.
              </p>
              <p>
                We do not use third-party advertising cookies or behavioural tracking cookies without your prior consent. Where we use optional analytics cookies, we will ask for your consent before activating them and you may withdraw that consent at any time via our cookie preferences.
              </p>
              <p>
                You can control cookies through your browser settings at any time. Please note that disabling essential cookies may affect the functionality of parts of the website.
              </p>
            </Section>

            <Section title="11. Security">
              <p>
                We implement appropriate technical and organisational measures to protect personal data against unauthorised access, accidental loss, destruction, or disclosure. These include encrypted data transmission (HTTPS/TLS), access controls, staff training, and regular security reviews.
              </p>
              <p>
                In the event of a personal data breach that is likely to result in a risk to your rights and freedoms, we will notify the ICO within 72 hours and, where required, notify affected individuals without undue delay.
              </p>
            </Section>

            <Section title="12. Changes to this policy">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or the law. The current version will always be published on this page with the date it was last updated. We encourage you to review this policy periodically.
              </p>
              <p>
                Where changes are significant, we will notify active clients and workers directly.
              </p>
            </Section>

          </div>

          {/* Contact */}
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 mt-6">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Privacy enquiries</h2>
            <p className="text-text-muted mb-6">To exercise your rights or make a privacy enquiry, contact our Data Protection lead:</p>
            <div className="space-y-3">
              <a href="mailto:info@osabeacares.co.uk" className="flex items-center gap-4 rounded-xl border border-[#E4E8EB] p-4 bg-[#F8FAFA] hover:border-primary/40 transition-colors group">
                <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <div className="text-xs text-text-muted">Email</div>
                  <div className="font-medium text-text-primary">info@osabeacares.co.uk</div>
                </div>
              </a>
              <a href="tel:01622809469" className="flex items-center gap-4 rounded-xl border border-[#E4E8EB] p-4 bg-[#F8FAFA] hover:border-primary/40 transition-colors group">
                <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                <div>
                  <div className="text-xs text-text-muted">Telephone</div>
                  <div className="font-medium text-text-primary">01622 809469</div>
                </div>
              </a>
            </div>
            <p className="text-sm text-text-muted mt-6">
              Or write to us at: Osabea Healthcare Solutions Limited, Suite FA4D, 1 St Faith's Street, Maidstone, Kent, ME14 1LH.
            </p>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
